import { GetStaticPropsResult } from 'next';
import { LayoutProps } from 'components/layout';
import { getMenus, menuAwareRevalidate } from 'lib/get-menus';
import {DrupalNode} from 'next-drupal';
import { DrupalJsonApiParams } from 'drupal-jsonapi-params';
import { drupal } from '@/lib/drupal';
import {testApiCompatibility} from "@/lib/test-api-compatibility";
import {ENTITY_TYPES, REVALIDATE_TTL} from '@/lib/entity-page/constants';
import {getWebforms} from "@/lib/get-webforms";
import getParagraphs from "@/lib/get-paragraphs";
import {getBlurMap} from "@/lib/get-blur-map";
import {BlurMap, CustomMenuLink} from "@/types/hygiena-types";
import {NodeLandingPage} from "@/components/templates/nodes/node--landing_page";
import {getAlertBanner} from "@/lib/get-alert-banner";
import {getDictionary} from "@/lib/dictionaries";
import structuredData from '@/data/places-structured.json';
import {getFeaturedProducts} from "@/lib/products";
import {currentSiteUrl} from "@/lib/public-site-url";

interface IndexPageProps extends LayoutProps {
  entity?: DrupalNode,
  blurMap?: BlurMap,
  metatag?: any,
  structuredData?: any,
  entityInfo?: {
    id?: string,
    type?: string,
  }
}

export default function IndexPage({ entity }: IndexPageProps) {
  return (
    <NodeLandingPage key={`index-${entity?.id}`} node={entity as DrupalNode}/>
  );
}

export async function getStaticProps(context): Promise<GetStaticPropsResult<IndexPageProps>> {
  const start = performance.now();
  const NEXT_BUILD_MODE = process.env?.["NEXT_BUILD_MODE"] ?? false;
  if (process.env.NODE_ENV == 'development') {
    try {
      await testApiCompatibility(ENTITY_TYPES, drupal);
    }
    catch (error) {
      console.log(error);
    }
  }

  let newsletterPad = false;
  const params = new DrupalJsonApiParams();

  if (context?.preview) {
    params.addCustomParam({'resourceVersion': context?.previewData?.resourceVersion});
  }

  const entity = await drupal.getResourceFromContext<DrupalNode>(
    'node--landing_page',
    context,
    {
      params: params.getQueryObject(),
    }
  );

  if (entity?.type === 'node--landing_page' && entity?.field_paragraphs) {
    await getParagraphs(entity, context);
  }

  // /{locale}/home redirects to the locale root.
  const siteUrl = currentSiteUrl();
  if (siteUrl) {
    const href = context.locale && context.locale !== context.defaultLocale
      ? `${siteUrl}/${context.locale}`
      : `${siteUrl}/`;
    for (const tag of entity?.metatag ?? []) {
      if (tag?.attributes?.rel === 'canonical') tag.attributes.href = href;
      if (tag?.attributes?.property === 'og:url') tag.attributes.content = href;
    }
  }

  // Homepage-only hero behavior: Talk to an Expert, and featured products in the carousel.
  if (entity?.field_paragraphs?.length) {
    const heroes = entity.field_paragraphs.filter((paragraph) => paragraph?.type === 'paragraph--hero');
    if (heroes[0]) {
      heroes[0].add_talk_to_expert = true;
    }
    const carouselHeroes = heroes.filter((paragraph) => paragraph?.field_hero_article_slider);
    if (carouselHeroes.length) {
      const featuredProducts = await getFeaturedProducts(context);
      for (const paragraph of carouselHeroes) {
        paragraph.field_hero_article_slider.field_has_articles = featuredProducts;
      }
    }
  }

  if (!NEXT_BUILD_MODE) {
    const end = performance.now();
    console.log(`Slug / took ${((end - start) / 1000).toFixed(2)} seconds to build.`)
  }

  const customMenuCTA: CustomMenuLink =  entity?.field_landing_page_cta;
  const menus = await getMenus(context);
  if (customMenuCTA?.title && customMenuCTA?.url && menus?.main?.length) {
    for (const item of menus.main) {
      if (item?.field_menu_item_cta) {
        item.title = customMenuCTA.title;
        item.url = customMenuCTA.url;
      }
    }
  }

  return {
    props: {
      entity: entity,
      webforms: await getWebforms(context),
      entityInfo: {
        id: entity.id,
        type: entity.type
      },
      alertBanner: await getAlertBanner(context),
      menus,
      blurMap: await getBlurMap(),
      metatag: entity?.metatag,
      newsletterPad: !newsletterPad,
      structuredData,
      dictionary: await getDictionary(context?.locale),
      translatedUrls: {
        en: '/',
        es: '/es',
        de: '/de',
        fr: '/fr',
        pl: '/pl',
        'pt-br': '/pt-br',
        th: '/th',
        'zh-hans': '/zh-hans',
      }
    },
    // Never pin a menu-less render for the full TTL.
    revalidate: menuAwareRevalidate(menus, parseInt(REVALIDATE_TTL)),
  };
}
